import { Button, Link } from "@opensea/next-ui-kit"
import {
  CenterAligned,
  Container,
  FlexCenter,
  FlexColumn,
  SpaceBetween,
  TextBody,
  TextDisplay,
} from "@opensea/ui-kit"
import { OpenSeaBrandLogo, OpenSeaLetters } from "@opensea/ui-kit/icons"
import { useAccountStates } from "@opensea/wallet"
import { useTranslations } from "next-intl"
import { useState } from "react"
import { AccountSwitcherDropdown } from "@/components/navigation/top-nav/account-switcher/AccountSwitcherDropdown"
import { ProfileButton } from "@/components/navigation/top-nav/ProfileButton"
import { SUPPORT_URL, TERMS_OF_SERVICE_URL } from "@/constants/constants"
import { useAddress } from "@/hooks/wallet"

export function AccountBannedPage() {
  const t = useTranslations("AccountBannedPage")
  const [accountSwitcherOpen, setAccountSwitcherOpen] = useState(false)

  return (
    <Container className="py-4">
      <SpaceBetween>
        <FlexCenter className="gap-2.5">
          <OpenSeaBrandLogo size={30} />
          <OpenSeaLetters className="h-8 translate-y-0.5" width={107} />
        </FlexCenter>
        <AccountSwitcherDropdown onOpenChange={setAccountSwitcherOpen}>
          <ProfileButton dropdownOpen={accountSwitcherOpen} />
        </AccountSwitcherDropdown>
      </SpaceBetween>
      <CenterAligned className="mx-auto h-[60vh] max-w-[700px]">
        <FlexColumn className="items-center gap-6 text-center">
          <AccountBannedHeading />
          <AccountBannedDescription />
          <Button href={SUPPORT_URL} variant="tertiary">
            {t("visitHelpCenter")}
          </Button>
        </FlexColumn>
      </CenterAligned>
    </Container>
  )
}

function AccountBannedHeading() {
  const t = useTranslations("AccountBannedPage")
  const accountStates = useAccountStates()
  // For now, presume only one account state
  const accountState = accountStates[0]

  function getText() {
    switch (accountState) {
      case "STATE_100":
      case "STATE_200":
      case "STATE_500":
        return t("accountDisabled")
      default:
        return t("accountUnavailable")
    }
  }

  return <TextDisplay size="lg">{getText()}</TextDisplay>
}

function AccountBannedDescription() {
  const t = useTranslations("AccountBannedPage")
  const address = useAddress()

  const accountStates = useAccountStates()
  // For now, presume only one account state
  const accountState = accountStates[0]

  const defaultSupportParagraph = (
    <TextBody>
      {t.rich("supportMessage", {
        link: children => <Link href={SUPPORT_URL}>{children}</Link>,
      })}
    </TextBody>
  )

  function renderText() {
    switch (accountState) {
      case "STATE_100":
        return (
          <>
            <TextBody>
              {t.rich("disabledViolation", {
                terms: children => (
                  <Link href={TERMS_OF_SERVICE_URL}>{children}</Link>
                ),
              })}
            </TextBody>

            {defaultSupportParagraph}
          </>
        )
      case "STATE_1000":
        return (
          <>
            <TextBody>
              {t.rich("disabledViolationCollections", {
                terms: children => (
                  <Link href={TERMS_OF_SERVICE_URL}>{children}</Link>
                ),
              })}
            </TextBody>

            {defaultSupportParagraph}
          </>
        )

      case "STATE_500":
        return (
          <>
            <TextBody>
              {t.rich("disabledViolationNotVisible", {
                terms: children => (
                  <Link href={TERMS_OF_SERVICE_URL}>{children}</Link>
                ),
              })}
            </TextBody>

            <TextBody>{t("collectionsNoLongerVisible")}</TextBody>

            {defaultSupportParagraph}
          </>
        )
      default:
        return (
          <>
            <TextBody>
              {t("accountUnavailableMessage", { address: address || "" })}
            </TextBody>

            <TextBody>{t("appreciatePatience")}</TextBody>
          </>
        )
    }
  }

  return (
    <FlexColumn className="gap-2 text-text-secondary">
      {renderText()}
    </FlexColumn>
  )
}
